'use client';

import { useEffect } from 'react';
import useSWR from 'swr';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useExitAnimation } from '../lib/useExitAnimation';
import Skeleton from './Skeleton';

type UserPublic = {
  id: number;
  name: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
};

type UserModalProps = {
  userId: number | null;
  onClose: () => void;
};

export default function UserModal({ userId, onClose }: UserModalProps) {
  const { t } = useTranslation();
  const { isAuthenticated, mounted } = useAuth();
  const exiting = useExitAnimation(userId != null);

  const { data: user, isLoading } = useSWR<UserPublic>(
    mounted && isAuthenticated && userId != null ? `/api/user/calendar/users/${userId}` : null,
  );

  useEffect(() => {
    if (userId == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [userId, onClose]);

  if (userId == null && !exiting) return null;

  const initial = (user?.firstName?.[0] || user?.email?.[0] || '?').toUpperCase();

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center bg-bark/40 backdrop-blur-sm overflow-y-auto ${userId != null ? 'animate-backdropIn' : 'animate-backdropOut'}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-modal="true" className={`bg-linen border border-wheat rounded-rustic shadow-xl p-6 w-full max-w-md mx-4 ${userId != null ? 'animate-modalIn' : 'animate-modalOut'}`}>
        {isLoading || !user ? (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <Skeleton height={64} width={64} className="rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton height={20} width="60%" />
                <Skeleton height={14} width="80%" />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-moss/20 flex items-center justify-center text-2xl font-bold text-moss-dark">
                {initial}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-bark truncate">{user.name}</h2>
                <a href={`mailto:${user.email}`} className="text-sm text-moss hover:text-moss-dark hover:underline truncate block">
                  {user.email}
                </a>
              </div>
            </div>

            <div className="space-y-3">
              {user.firstName && (
                <div className="flex justify-between border-b border-wheat pb-2">
                  <span className="text-sm text-wood">{t('profileFirstName')}</span>
                  <span className="text-sm text-bark font-medium">{user.firstName}</span>
                </div>
              )}
              {user.lastName && (
                <div className="flex justify-between border-b border-wheat pb-2">
                  <span className="text-sm text-wood">{t('profileLastName')}</span>
                  <span className="text-sm text-bark font-medium">{user.lastName}</span>
                </div>
              )}
              <div className="flex justify-between border-b border-wheat pb-2">
                <span className="text-sm text-wood">{t('profileEmail')}</span>
                <a href={`mailto:${user.email}`} className="text-sm text-moss hover:text-moss-dark font-medium">
                  {user.email}
                </a>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end pt-4 border-t border-wheat/30 mt-4">
          <button onClick={onClose} className="btn-secondary px-4 py-2">
            {t('calendarCancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
